(() => {
  const sourceSelector = '[role="dialog"][aria-modal="true"], [data-fp-modal-a11y]';
  const managedDialogs = new Set();
  const openState = new WeakMap();
  const openers = new WeakMap();
  let pendingOpener = null;
  let frame = 0;

  const isVisible = (element) => {
    if (element.hidden || element.closest('[hidden], [aria-hidden="true"]')) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  };

  const isOpen = (dialog) => {
    const nativeDialog = dialog.closest('dialog');
    if (nativeDialog) return nativeDialog.open;

    const details = dialog.closest('details');
    if (details) return details.open;

    const modal = dialog.closest('modal-dialog, quick-add-modal, product-modal');
    if (modal) return modal.hasAttribute('open');

    const pickupDrawer = dialog.closest('pickup-availability-drawer');
    if (pickupDrawer) return pickupDrawer.hasAttribute('open');

    const cartDrawer = dialog.closest('cart-drawer');
    if (cartDrawer) return cartDrawer.classList.contains('active');

    const notification = dialog.closest('#cart-notification');
    if (notification) return notification.classList.contains('active');

    return isVisible(dialog);
  };

  const collect = (scope = document) => {
    const dialogs = scope.matches?.(sourceSelector)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(sourceSelector) || []);
    dialogs.forEach((dialog) => managedDialogs.add(dialog));
  };

  const sync = () => {
    collect();
    managedDialogs.forEach((dialog) => {
      if (!dialog.isConnected) {
        managedDialogs.delete(dialog);
        return;
      }
      const open = isOpen(dialog);
      const wasOpen = openState.get(dialog) === true;
      if (open) {
        dialog.setAttribute('aria-modal', 'true');
        if (!wasOpen && pendingOpener?.isConnected && !dialog.contains(pendingOpener)) {
          openers.set(dialog, pendingOpener);
        }
      } else {
        dialog.removeAttribute('aria-modal');
        if (wasOpen) {
          const active = document.activeElement;
          const focusWasLost = !active || active === document.body || dialog.contains(active);
          const opener = openers.get(dialog);
          if (focusWasLost && opener?.isConnected) opener.focus({ preventScroll: true });
        }
      }
      openState.set(dialog, open);
    });
    pendingOpener = null;
  };

  const schedule = () => {
    if (frame) window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      sync();
    });
  };

  sync();
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[aria-haspopup="dialog"], summary, button, [role="button"]');
    if (trigger) pendingOpener = trigger;
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') pendingOpener = document.activeElement;
  }, true);
  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'hidden', 'open', 'aria-hidden', 'style']
  });
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:unload', () => schedule());
  document.addEventListener('shopify:block:select', schedule);
})();
